import * as PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { matchPath } from 'react-router';
import {
  branch,
  compose,
  getContext,
  lifecycle,
  mapProps,
  renderComponent,
  withProps,
  withState,
} from 'recompose';

import ErrorMessage from '../../components/ErrorMessage';
// presentation components
import LoadingAnimation from '../../components/LoadingAnimation';
import TranslationsContainer from '../../sections/Translations';
import { SocketMessages } from '../../types';
import {
  loadConversationsList,
  mapCounter,
  setActiveConversation,
  updateConversationsList,
  updateUnreadCount,
} from './ConversationsListActions';
import ConversationsList from './ConversationsListComponent';
import ConversationListPlaceholder from './ConversationsListPlaceholder';
import { ConversationsListContainerProps } from './ConversationsListTypes';

const getFilteredConversations = (conversationsListStore, searchValue) => {
    if ((conversationsListStore && conversationsListStore.conversationsList.length) && searchValue) {
        return conversationsListStore.conversationsList.filter((conversation) => (
            (conversation.recipient.name.toLowerCase().includes(searchValue.toLowerCase()) ||
            conversation.recipient.phone_number.includes(searchValue))
        ));
    }

    return conversationsListStore.conversationsList || [];
};

const mapStateToProps = ({ conversationsListStore }) => ({ conversationsListStore });
const filterSelected  = conversation => Boolean(matchPath(window.location.pathname.split('/messenger').join(''), { path: `/conversation/${conversation.id}` }));

const isLoading  = ({ conversationsListStore }) => conversationsListStore.loading && !conversationsListStore.conversationsList.length;
const hasError   = ({ conversationsListStore }) => conversationsListStore.error;
const hasNoConvo = ({ conversationsListStore }) => conversationsListStore.conversationsList.length === 0;

const LoadingRender     = renderComponent(LoadingAnimation);
const ErrorRender       = renderComponent(ErrorMessage);
const PlaceholderRender = renderComponent(ConversationListPlaceholder);

function updateUnreadMessages(props: ConversationsListContainerProps) {
    const selected = props.conversationsListStore.conversationsList && props.conversationsListStore.conversationsList.find(filterSelected);

    if (selected && parseInt(selected.unread_message_count, 10)) {
        props.dispatch(updateUnreadCount(selected.id, selected.last_message.id));
    }
}

const ConversationListContainer = compose(
    TranslationsContainer,
    connect(mapStateToProps),
    getContext({
        session: PropTypes.any.isRequired,
        socket: PropTypes.object.isRequired,
        onDesktop: PropTypes.bool
    }),
    withState('searchValue', 'setSearchValue', ''),
    withState('filterState', 'setFilterState', 'outlet_manager'),
    mapProps(({ conversationsListStore, searchValue, ...ownProps}) => { // handle filtering logic in container
        const filteredConversations = getFilteredConversations(conversationsListStore, searchValue);
        const unreadConversations = conversationsListStore.unread;
        const inboxPlaceholderName = conversationsListStore.inboxPlaceholderName;
        return {
            inboxPlaceholderName,
            filteredConversations,
            conversationsListStore,
            unreadConversations,
            loading: conversationsListStore.loading || false,
            searchValue,
            ...ownProps
        };
    }),
    withProps((ownProps: ConversationsListContainerProps) => ({
        handleMessageUpdate() {
            ownProps.dispatch(updateConversationsList(ownProps.filterState));
        },
        handleClick(conversationId: string) {
            const hasUnreadItem = ownProps.conversationsListStore.conversationsList && ownProps.conversationsListStore.conversationsList.find(conversation =>
                Boolean(conversation.id === conversationId && parseInt(conversation.unread_message_count, 10))
            );

            hasUnreadItem && ownProps.dispatch(updateUnreadCount(conversationId, hasUnreadItem.last_message.id));
            ownProps.dispatch(mapCounter(conversationId));
            ownProps.dispatch(setActiveConversation(conversationId));
        },
        handleChangeFilter: ({ target }) => ownProps.setFilterState(target.value)
    })),
    lifecycle<ConversationsListContainerProps, {}>({
        componentWillMount() {
            const loadedConversationsList = this.props.conversationsListStore.conversationsList;

            if (loadedConversationsList.length) {
                return loadedConversationsList;
            }

            this.props.dispatch(loadConversationsList(this.props.filterState));
        },
        componentDidMount() {
            updateUnreadMessages(this.props);
            this.props.socket.on(SocketMessages.UPDATE, this.props.handleMessageUpdate);
            this.props.socket.on(SocketMessages.ERROR, this.props.handleMessageUpdate);
        },
        componentDidUpdate(prevProps) {
            updateUnreadMessages(this.props);

            if (prevProps.filterState !== this.props.filterState) {
                this.props.dispatch(loadConversationsList(this.props.filterState));
            }
        },
        componentWillUnmount() {
            this.props.socket.off(SocketMessages.UPDATE, this.props.handleMessageUpdate);
            this.props.socket.off(SocketMessages.ERROR, this.props.handleMessageUpdate);
        }
    }),
    branch(isLoading, LoadingRender),
    branch(hasError, ErrorRender),
    branch(hasNoConvo, PlaceholderRender)
);

export default ConversationListContainer(ConversationsList) as React.ComponentClass<ConversationsListContainerProps>;



// WEBPACK FOOTER //
// ./src/sections/ConversationsList/index.ts